import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import { swapOperator } from '../../filterQueryFormat/operator/Operator';
import { makeConditionHashKey } from '../../hooks/useNullValueState';
import { useContext, useMemo } from 'react';
import { useOperatorOptions } from '../../hooks/useOperatorOptions';
import DefaultNullValueRecord from '../../filterQueryFormat/DefaultNullValueRecord';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import UISelect from 'UIComponents/input/UISelect';

// Some systems and objects/events cannot support these operators
import { jsx as _jsx } from "react/jsx-runtime";
const DISALLOWED_HISTORICAL = [Operators.EverContained, Operators.NeverContained, Operators.EverContainedAll, Operators.NeverContainedAll, Operators.EverEqual, Operators.NeverEqual, Operators.EverEqualAll, Operators.NeverEqualAll, Operators.EverEqualAny, Operators.NeverEqualAny, Operators.EverIn, Operators.NeverIn];

// Some systems and objects/events cannot support these operators
// or they do not make sense for them to be shown
// Example: Unified Events are immutable so its impossible for properties to be updated
const DISALLOWED_FOR_IMMUTABLE_PROPERTIES = [...DISALLOWED_HISTORICAL, Operators.UpdatedAfter, Operators.UpdatedBefore, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays];

// Events that should not be shown for Unified Events
const DISSALLOWED_FOR_UNIFIED_EVENTS = [...DISALLOWED_HISTORICAL, ...DISALLOWED_FOR_IMMUTABLE_PROPERTIES, Operators.After, Operators.Before, Operators.ContainAll, Operators.NotContainAll, Operators.NotEqualAll];
const OperatorSelect = ({
  condition,
  conditionPath,
  disabled,
  limitOperators,
  filterFamily,
  inputUse,
  property,
  onChange,
  readOnly
}) => {
  const {
    nullValueState
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    options,
    getOperatorByName
  } = useOperatorOptions(filterFamily, {
    field: property
  });
  const operatorName = condition ? condition.name : undefined;
  const operatorOptions = useMemo(() => {
    if (limitOperators === 'unifiedEvents') {
      return options.filter(({
        value
      }) => !DISSALLOWED_FOR_UNIFIED_EVENTS.some(operator => operator.toString() === value));
    }
    if (limitOperators === 'immutable') {
      return options.filter(({
        value
      }) => !DISALLOWED_FOR_IMMUTABLE_PROPERTIES.some(operator => operator.toString() === value));
    }
    if (limitOperators === 'historical') {
      return options.filter(({
        value
      }) => !DISALLOWED_HISTORICAL.some(operator => operator.toString() === value));
    }
    return options;
  }, [limitOperators, options]);
  const onSelectedOptionChange = evt => {
    const Operator = getOperatorByName(evt.target.value.value);
    if (!condition || !conditionPath) {
      const newCondition = Operator.of(property);
      onChange(newCondition);
      return;
    }
    const newCondition = swapOperator(Operator, condition);
    const hashKey = makeConditionHashKey(newCondition, conditionPath);
    const defaultNullValue = nullValueState.defaultNullValueByOperator[hashKey] || DefaultNullValueRecord();
    onChange(newCondition.set('defaultNullValue', defaultNullValue));
  };
  return /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "property-operator-select",
    disabled: disabled,
    buttonUse: inputUse === 'on-dark' ? 'form-on-dark' : undefined,
    onSelectedOptionChange: onSelectedOptionChange,
    options: operatorOptions,
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFiltersUI.propertyFilter.operatorSelect.placeholder"
    }),
    readOnly: readOnly || operatorOptions.length === 1,
    value: operatorName
  });
};
export default OperatorSelect;